// Dynamic entity naming (design 2026-07-14) — the single source of truth for
// the generic-slug reject list and the name-provenance priority model. Shared
// by the CLI app-create path, the API entity-create route, and the drop slug
// pipeline so all three reject the same generic words and rank name sources the
// same way.
//
// Two independent bugs this closes (spec §1.4):
//   1. the app path (`suggestSlug(basename(cwd))`) had no generic reject, so a
//      folder named `app` / `dist` / `index` became the permanent slug.
//   2. the inspiration filename tier deliberately skipped the guard, so
//      `index.html` → `index`.
//
// Both now import `isGenericSlug` from here.

/**
 * Generic-slug reject list. A slug that normalizes to one of these carries no
 * intent about the entity — it is the name every anonymous drop or bare folder
 * converges on. The first block is the drop pipeline's locked v1 list
 * (spec 2026-06-05-drop-slug-and-naming §9.2); the second block adds the
 * build-artifact / scaffold folder names an app-create path converges on
 * (`suggestSlug('dist')` → `dist`). Whole-slug match only — a compound like
 * `index-page` or `my-app` is fine.
 */
export const GENERIC_SLUGS: ReadonlySet<string> = new Set([
  // ── drop pipeline locked v1 list (spec 2026-06-05 §9.2) ──
  'page', 'index', 'main', 'untitled', 'demo', 'doc', 'docs',
  'home', 'output', 'file', 'tmp', 'temp', 'test', 'example',
  'unnamed', 'new', 'draft', 'claude', 'gpt', 'chat', 'conversation',
  'response', 'code', 'script', 'app',
  // ── app create-path build/scaffold generics (dynamic-entity-naming §3.1) ──
  // Kept tight to genuine build-artifact / source folder names the spec names
  // (§1.2 prose + §6.1 sentinel list); intentionally NOT words a user might
  // legitimately mean as an app name (e.g. `static`, `site`, `web`).
  'dist', 'build', 'src', 'out', 'node-modules', 'drop', 'dst',
]);

/** True iff `slug` (case-insensitively) is a bare generic word. */
export function isGenericSlug(slug: string): boolean {
  return GENERIC_SLUGS.has(slug.toLowerCase());
}

/**
 * Name-provenance sources, highest priority (never overridden) first. This is
 * the priority ladder from the design §2.1:
 *   human            — `--name`, yaml `name:`, drop `title` (terminal)
 *   conversation     — name/description supplied in the MCP plug conversation
 *   inferred-content — inspiration served-content inference
 *   inferred-app     — cheap deeper read of yaml + repo signals
 *   fallback-generic — last-resort typed placeholder (never a bare generic word)
 */
export type NameSource =
  | 'human'
  | 'conversation'
  | 'inferred-content'
  | 'inferred-app'
  | 'fallback-generic';

const NAME_SOURCE_RANK: Record<NameSource, number> = {
  human: 1,
  conversation: 2,
  'inferred-content': 3,
  'inferred-app': 4,
  'fallback-generic': 5,
};

/** Numeric rank of a name source (1 = highest priority). */
export function nameSourceRank(source: NameSource): number {
  return NAME_SOURCE_RANK[source];
}

/**
 * The single naming invariant (design §4): a name may only be replaced by a
 * signal of strictly higher priority. `human` and `conversation` are terminal
 * — no inference (3-5) can ever override them, which falls out of the rank
 * comparison. An absent existing source is always writable (first write).
 *
 * This generalizes the existing two-state `isWritable` guard (human vs brain)
 * to the five-level priority ladder.
 */
export function canOverrideName(
  existing: NameSource | undefined,
  incoming: NameSource,
): boolean {
  if (existing === undefined) return true;
  return NAME_SOURCE_RANK[incoming] < NAME_SOURCE_RANK[existing];
}

/**
 * Build a typed, non-generic placeholder slug from a prefix and a short random
 * suffix, e.g. `('next-app', '3a8f')` → `next-app-3a8f`. If the prefix alone is
 * generic (or empty), the suffix makes the whole slug non-generic (`app-3a8f`
 * is not a bare `app`). Normalizes the prefix to slug-safe characters and caps
 * the whole result at 40 chars (the entity-slug ceiling) without leaving a
 * trailing hyphen.
 */
export function buildFallbackSlug(prefix: string, suffix: string): string {
  const cleanPrefix = prefix
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^[^a-z]+/, '');
  const base = cleanPrefix.length >= 1 ? cleanPrefix : 'app';
  const suffixPart = suffix.toLowerCase().replace(/[^a-z0-9]/g, '') || '0000';
  const maxPrefix = 40 - suffixPart.length - 1;
  const trimmed = base.slice(0, Math.max(1, maxPrefix)).replace(/-+$/, '');
  return `${trimmed}-${suffixPart}`;
}
